import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, X, CheckCircle2, Loader2, User, ExternalLink } from 'lucide-react';

/* iter536 · Story content-block <-> athlete/network link.
 *
 * Shared editor + renderer used by ContentBlockEditor and
 * ContentBlockRenderer. Lets Jay drop a "Mention" chip into any story
 * that links directly to an athlete's public /network/{slug} page in
 * a sleek Liquid Glass card. Search runs against /api/athletes?q=…
 * (same slim endpoint the admin rotation picker uses), so the surface
 * always mirrors the live network.
 *
 * Block shape:  { type:'athlete_mention', data:{ slug, name, photo,
 *                                                sport, team, note } }
 * Only `slug` is required — everything else is denormalized so the
 * renderer never has to make a second fetch (fast on iOS, resilient
 * to the athlete row being soft-deleted).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── EDITOR ─────────────────────────────────────────────────────── */

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent';

export const AthleteMentionEditor = ({ data, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const clearSelection = () => onChange({});

  const runSearch = useCallback((q) => {
    setSearching(true);
    axios.get(`${API}/athletes`, { params: { q, slim: true, limit: 10 } })
      .then((r) => setResults(Array.isArray(r.data) ? r.data : []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return undefined; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(q), 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [query, runSearch]);

  const pick = (a) => {
    onChange({
      slug: a.slug,
      name: a.name || '',
      photo: a.photo || a.network_hero_image || '',
      sport: a.sport || '',
      team: a.team || a.school || '',
      note: data.note || '',
    });
    setQuery('');
    setResults([]);
  };

  const hasPick = !!data?.slug;

  return (
    <>
      {hasPick ? (
        <div className="p-3 rounded-xl bg-black/[0.03] border border-gray-200 flex items-center gap-3 mb-3" data-testid="athlete-mention-selected">
          <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-neutral-800 to-neutral-950 flex items-center justify-center flex-shrink-0">
            {data.photo ? (
              <img src={data.photo} alt="" className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <User className="w-5 h-5 text-white/60" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <p className="text-sm font-bold truncate">{data.name || data.slug}</p>
            </div>
            <p className="text-[11px] text-gray-500 truncate">
              {[data.sport, data.team].filter(Boolean).join(' · ') || `/network/${data.slug}`}
            </p>
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-full hover:bg-red-50"
            data-testid="athlete-mention-clear"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="mb-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
            Search athletes on the network
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, team, or slug…"
              className={`${inputCls} pl-9`}
              data-testid="athlete-mention-search-input"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setResults([]); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searching && (
            <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </p>
          )}
          {!searching && results.length > 0 && (
            <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto bg-white shadow-sm" data-testid="athlete-mention-results">
              {results.map((a) => (
                <li key={a.slug}>
                  <button
                    type="button"
                    onClick={() => pick(a)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
                    data-testid={`athlete-mention-result-${a.slug}`}
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {(a.photo || a.network_hero_image) ? (
                        <img src={a.photo || a.network_hero_image} alt="" className="w-full h-full object-cover"
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <User className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{a.name || a.slug}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {[a.sport, a.team || a.school].filter(Boolean).join(' · ') || `/network/${a.slug}`}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!searching && query.trim() && results.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">No athletes match &ldquo;{query}&rdquo;.</p>
          )}
        </div>
      )}

      {hasPick && (
        <div className="mb-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
            Optional caption
          </label>
          <input
            type="text"
            value={data.note || ''}
            onChange={(e) => onChange({ ...data, note: e.target.value })}
            placeholder="e.g. Signed his LOI · Class of 2027"
            className={inputCls}
            maxLength={140}
            data-testid="athlete-mention-note-input"
          />
          <p className="text-xs text-gray-500 mt-1">Renders below the athlete&rsquo;s name, italic.</p>
        </div>
      )}
    </>
  );
};

/* ─── RENDERER ───────────────────────────────────────────────────── */

/**
 * Liquid Glass Athlete card. Tapping anywhere opens /network/{slug}
 * in the same tab so we keep native iOS in-app navigation (no target
 * blank -> forced Safari swap).
 */
export const AthleteMentionBlock = ({ data }) => {
  if (!data?.slug) return null;
  const displayName = data.name || data.slug;
  const meta = [data.sport, data.team].filter(Boolean).join(' · ');

  return (
    <Link
      to={`/network/${data.slug}`}
      className="cb-athlete-mention block group"
      data-testid={`cb-athlete-mention-${data.slug}`}
      style={{
        position: 'relative',
        display: 'block',
        margin: '1.25rem 0',
        padding: '1px',
        borderRadius: '22px',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.02) 40%, rgba(255,255,255,0.10) 100%)',
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          borderRadius: '21px',
          padding: '14px 16px',
          background: 'linear-gradient(180deg, rgba(20,20,22,0.72) 0%, rgba(10,10,12,0.86) 100%)',
          backdropFilter: 'blur(24px) saturate(160%)',
          WebkitBackdropFilter: 'blur(24px) saturate(160%)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 20px 40px -24px rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Liquid highlight sheen */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '55%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.06), transparent)',
            pointerEvents: 'none',
          }}
        />
        {/* Avatar */}
        <div
          style={{
            width: 52, height: 52, borderRadius: '50%', overflow: 'hidden',
            flexShrink: 0, position: 'relative',
            background: 'linear-gradient(135deg, #1a1a1e 0%, #050506 100%)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14)',
          }}
        >
          {data.photo ? (
            <img
              src={data.photo}
              alt={displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <User style={{ width: 22, height: 22, color: 'rgba(255,255,255,0.5)', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
          )}
        </div>
        {/* Copy */}
        <div style={{ minWidth: 0, flex: 1, position: 'relative' }}>
          <p style={{
            fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase',
            fontWeight: 800, color: 'rgba(153,246,228,0.95)',
            margin: 0, lineHeight: 1,
          }}>
            On the Network
          </p>
          <p style={{
            fontSize: 16, fontWeight: 800, color: '#fff',
            margin: '4px 0 0', lineHeight: 1.15,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {displayName}
          </p>
          {meta && (
            <p style={{
              fontSize: 11, color: 'rgba(255,255,255,0.55)', margin: '2px 0 0',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {meta}
            </p>
          )}
          {data.note && (
            <p style={{
              fontSize: 12, fontStyle: 'italic', color: 'rgba(255,255,255,0.72)',
              margin: '6px 0 0', lineHeight: 1.35,
            }}>
              {data.note}
            </p>
          )}
        </div>
        {/* Chevron */}
        <span
          style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, position: 'relative',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.10)',
          }}
        >
          <ExternalLink style={{ width: 13, height: 13, color: 'rgba(255,255,255,0.85)' }} />
        </span>
      </div>
    </Link>
  );
};

export const ATHLETE_MENTION_DEFAULT = { slug: '', name: '', photo: '', sport: '', team: '', note: '' };
